import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isEmpty } from 'lodash-es';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { IncentivesEntities } from 'client/data/models/incentives';
import { stripInvalidHTML, stripHTML } from 'site-modules/shared/utils/strip-html';
import { getTransactionTypes, getIncentiveTitle } from 'client/site-modules/shared/utils/incentives';
import { INCENTIVE_TYPES, INCENTIVE_HEAD_TITLES } from 'site-modules/shared/constants/incentives';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { IncentiveRebateInfo } from './incentive-rebate-info';
import { IncentiveFinancingInfo } from './incentive-financing-info';
import { IncentiveLeasingInfo } from './incentive-leasing-info';
import { IncentiveOtherInfo } from './incentive-other-info';

import './incentive-info.scss';

const COLLAPSE_LENGTH = 570;

const INFO_COMPONENT_MAP = {
  [INCENTIVE_TYPES.REBATES]: IncentiveRebateInfo,
  [INCENTIVE_TYPES.FINANCING]: IncentiveFinancingInfo,
  [INCENTIVE_TYPES.LEASING]: IncentiveLeasingInfo,
  [INCENTIVE_TYPES.OTHER]: IncentiveOtherInfo,
};

export function IncentiveInfo({ incentive = {}, type, isLast = false, isMobile = false }) {
  if (isEmpty(incentive)) {
    return null;
  }

  const { id, restrictions: initRestrictions, details, type: incentiveType } = incentive;

  const InfoView = INFO_COMPONENT_MAP[type];
  const titleType = type === INCENTIVE_TYPES.REBATES ? incentiveType : type;
  const title = get(INCENTIVE_HEAD_TITLES, `${titleType}[0]`, '');
  const availableForTypes = getTransactionTypes(incentive);

  const description = (
    <Fragment>
      {availableForTypes && <span className="d-block pb-0_5">Available for: {availableForTypes}</span>}
      <span>Requirements and Restrictions:</span>
      {!!initRestrictions && (
        <ContentFragment classes={classnames({ medium: isMobile })}>
          {stripInvalidHTML(initRestrictions)}
        </ContentFragment>
      )}
      {!!details && (
        <ContentFragment classes={classnames({ 'mt-0_5': initRestrictions, medium: isMobile })}>
          {stripInvalidHTML(details)}
        </ContentFragment>
      )}
    </Fragment>
  );
  const scrollTop = `scrollTop-incentive-${id}`;

  return (
    <section className={classnames('incentive-info pb-0_5', { 'mb-1_5 mb-md-2': !isLast })}>
      <h4 className="heading-4 fw-medium">{getIncentiveTitle({ ...incentive, name: title })}</h4>
      {(initRestrictions || details) &&
        (get(stripHTML(details), 'length', 0) + get(stripHTML(initRestrictions), 'length', 0) > COLLAPSE_LENGTH ? (
          <Fragment>
            <ScrollElement id={scrollTop} />
            <ContentCollapse
              textClosed="Read more"
              textOpen="Read less"
              justify="justify-content-start"
              btnContainerClasses="mt-0_5 py-0"
              classes="pb-0_5"
              btnClasses="px-0_5 py-0 size-16 text-primary-darker"
              btnTextClasses="fw-normal"
              trackingId={null}
              btnTrackingId="incentive_info"
              minHeight="140px"
              showBtnBelow
              isEclipseFade={false}
              resetParams={{
                offset: SCROLL_OFFSET,
                duration: SCROLL_DURATION,
                to: scrollTop,
              }}
            >
              {description}
            </ContentCollapse>
          </Fragment>
        ) : (
          <div className="pb-0_5">{description}</div>
        ))}
      <InfoView incentive={incentive} titleType={titleType} isLast={isLast} />
    </section>
  );
}

IncentiveInfo.propTypes = {
  type: PropTypes.string.isRequired,
  incentive: IncentivesEntities.IncentiveGraphql,
  isLast: PropTypes.bool,
  isMobile: PropTypes.bool,
};
